import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { outfitAdminApi } from '../../../../services/outfitAdminApi';

function Step2SelectCCI({ gender, selectedCCI, onSelectCCI, onNext, onBack }) {
  const [loading, setLoading] = useState(true);
  const [activeCCIs, setActiveCCIs] = useState([]);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const fetchActiveList = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await outfitAdminApi.fetchActiveCCIs(gender);
      if (response && Array.isArray(response.data)) {
        setActiveCCIs(response.data);
      }
    } catch (err) {
      console.error('Failed to load active CCIs:', err);
      setError(err.message || 'Failed to load active category combinations');
    } finally {
      setLoading(false);
    }
  }, [gender]);

  useEffect(() => {
    fetchActiveList();
  }, [fetchActiveList]);

  const filteredList = useMemo(() => {
    if (!search.trim()) return activeCCIs;
    const q = search.toLowerCase().trim();
    return activeCCIs.filter((item) => {
      // Match ID
      if (item.category_combination_id?.toLowerCase().includes(q)) return true;
      // Match occasions
      if (item.occasions?.some((occ) => occ.toLowerCase().includes(q))) return true;
      // Match categories
      if (item.combination) {
        return Object.values(item.combination).some((val) => {
          if (Array.isArray(val)) {
            return val.some((v) => String(v).toLowerCase().includes(q));
          }
          return String(val).toLowerCase().includes(q);
        });
      }
      return false;
    });
  }, [activeCCIs, search]);

  return (
    <div className="outfit-card">
      <div className="outfit-card-header">
        <div>
          <h2 className="outfit-card-title">Step 2: Select Outfit Category Combination (CCI)</h2>
          <span style={{ fontSize: '13px', color: '#64748b' }}>
            Choose an active base outfit formula for <strong>{gender}</strong>
          </span>
        </div>
      </div>

      {error && (
        <div className="outfit-alert error">
          <span>{error}</span>
        </div>
      )}

      <div style={{ marginBottom: '16px' }}>
        <input
          type="text"
          className="cci-search-input"
          style={{ width: '100%', boxSizing: 'border-box' }}
          placeholder="Filter formulas by categories (e.g. Jeans, T-Shirts) or occasions..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="outfit-loading-state" style={{ minHeight: '200px' }}>
          <div className="outfit-spinner" />
          <p>Loading active category combination formulas...</p>
        </div>
      ) : filteredList.length === 0 ? (
        <div className="outfit-empty-state" style={{ padding: '40px 20px' }}>
          <p>No active CCIs found for {gender}.</p>
        </div>
      ) : (
        <div className="wizard-cci-grid">
          {filteredList.map((item) => {
            const isSelected =
              selectedCCI?.category_combination_id === item.category_combination_id;
            return (
              <div
                key={item.category_combination_id}
                className={`wizard-cci-card ${isSelected ? 'selected' : ''}`}
                onClick={() => onSelectCCI(item)}
              >
                <div className="wizard-cci-header">
                  <span className="cci-id-hash" style={{ fontSize: '11px' }}>
                    {item.category_combination_id.slice(0, 10)}...
                  </span>
                  <span className="wizard-looks-badge">
                    {item.total_looks_count || 0} looks available
                  </span>
                </div>

                <div className="cci-formula-grid" style={{ marginBottom: '10px' }}>
                  {item.combination &&
                    Object.entries(item.combination).map(([slot, categoryVal]) => {
                      const valStr = Array.isArray(categoryVal)
                        ? categoryVal.join(', ')
                        : categoryVal;
                      return (
                        <span key={slot} className="cci-slot-pill">
                          <span className="cci-slot-label">{slot.replace(/_/g, ' ')}:</span>
                          <span className="cci-slot-value">{valStr}</span>
                        </span>
                      );
                    })}
                </div>

                {Array.isArray(item.occasions) && item.occasions.length > 0 && (
                  <div className="cci-occasions-wrap" style={{ marginTop: 'auto' }}>
                    {item.occasions.slice(0, 4).map((occ, idx) => (
                      <span key={idx} className="cci-occasion-tag">
                        {occ}
                      </span>
                    ))}
                    {item.occasions.length > 4 && (
                      <span className="cci-occasion-tag">
                        +{item.occasions.length - 4} more
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="wizard-nav-bar">
        <button type="button" className="outfit-btn outfit-btn-secondary" onClick={onBack}>
          &larr; Back to Basics
        </button>
        <button
          type="button"
          className="outfit-btn outfit-btn-primary"
          disabled={!selectedCCI}
          onClick={onNext}
        >
          Next: Curate Visual Facets &rarr;
        </button>
      </div>
    </div>
  );
}

export default Step2SelectCCI;
